import type { ClashResult } from '@viewer/clash/lib'
import { clashKey } from '@viewer/clash/lib'
import type { ClashSide } from './clashSets'
import {
  temDecisao,
  type ClashEvento,
  type ClashTriageEntry,
  type ClashTriageMap,
  type ClashUltimoConhecido,
} from './clashTriage'

export type { ClashUltimoConhecido }

// ---------------------------------------------------------------------------
// Reconciliacao: repetir a corrida actualiza a triagem que ja existia.
//
// Para cada conflito com triagem:
//
//   - continua a ocorrer: fica com o estado. Se estava "resolvido",
//     passa a "reaberto" e fica um evento "Reapareceu na corrida de ...".
//   - deixou de ocorrer numa corrida COMPARAVEL: passa a "resolvido",
//     com o evento "Deixou de ocorrer na corrida de ... (revisoes: ...)".
//     Comparavel: os mesmos conjuntos, a mesma tolerancia, a mesma folga
//     e o mesmo teste dentro do modelo que a corrida anterior; os modelos
//     dos dois elementos carregados; os dois elementos ainda existem
//     (pelo GlobalId). Um elemento que ja nao existe tambem resolve, mas
//     o evento diz "elemento removido".
//   - deixou de ocorrer numa corrida NAO comparavel: nao muda, e fica
//     "por verificar: corrida nao comparavel", com a razao.
//
// Os grupos (da proposta) ficam resolvidos quando todos os pares estao:
// e o que o topico BCF do grupo ja faz (fica com o estado menos
// avancado); o painel e a descricao dizem "x de y resolvidos".
//
// IDENTIDADE ENTRE REVISOES. A chave canonica usa o localId do Fragments,
// que muda quando o IFC e exportado de novo. Antes de reconciliar,
// `migrarPorGuids` passa a triagem para a chave nova quando o par de
// GlobalId (e o tipo, duro ou folga) e o mesmo: o conflito continua a ser
// o mesmo, mesmo que o ficheiro tenha outro nome ou outros localId.
//
// Puro: quem chama da os conflitos da corrida, a triagem, a configuracao
// das duas corridas e uma funcao que diz onde esta um GlobalId.
// ---------------------------------------------------------------------------

/** Configuracao de uma corrida: o que a torna comparavel com outra. */
export interface ClashConfigCorrida {
  tolerance: number
  clearance: number
  selfClash: boolean
  sideA: ClashSide
  sideB: ClashSide
  /** Filtro a entrada (clashFiltroEntrada.ts). Ausentes nas corridas
   *  anteriores ao funil: le-se penetracao 0, todos os pares na folga e
   *  sem corte de contactos, que era o que essas corridas faziam. */
  penetracaoMinima?: number
  folgaPares?: string[]
  ignoreContacts?: number
}

export interface ClashContagens {
  total: number
  novo: number
  aceite: number
  resolvido: number
  reaberto: number
  porVerificar: number
}

export interface ResumoReconciliacao {
  resolvidos: number
  reabertos: number
  novos: number
  porVerificar: number
}

/** Uma corrida no historico (fica na sessao). */
export interface ClashCorrida {
  data: string
  modelos: { ficheiro: string; sha256: string }[]
  config: ClashConfigCorrida
  contagens: ClashContagens
  resumo?: ResumoReconciliacao
  /** O metodo de agrupamento, os parametros e as regras em vigor nesta
   *  corrida (para se saber, depois, com que leitura foi triada). */
  agrupamento?: {
    metodo: string
    parametros: Record<string, unknown>
    regras: Record<string, unknown>
    grupos: number
  }
}

/** Topicos enviados para o modulo BCF: a reconciliacao actualiza-os,
 *  depois de confirmada. */
export interface ClashEnvioModulo {
  bcfLocalId: string
  /** Por topico (chave do item: clashKey ou "grupo:<id>"). `desde`: os
   *  topicos acrescentados depois do envio ("Acrescentar os novos ao
   *  BCF") ja levaram os eventos ate essa data nos comentarios; so os
   *  posteriores se propoem. */
  topicos: Record<string, { guid: string; chaves: string[]; desde?: string }>
  /** ISO do ultimo "Aplicar ao BCF": os eventos posteriores ainda nao
   *  foram para os comentarios. */
  aplicadoAte: string
}

function ordenado(v: readonly string[]): string[] {
  return [...v].sort()
}

function chaveDoLado(s: ClashSide): string {
  return JSON.stringify({
    source: s.source,
    models: ordenado(s.models),
    categories: ordenado(s.categories.map((c) => c.toUpperCase())),
    rule: s.source === 'rule' ? (s.rule ?? null) : null,
  })
}

/** Os conjuntos sao os mesmos? (A e B pela ordem; trocar A com B e
 *  outra corrida.) */
export function mesmosConjuntos(a: ClashConfigCorrida, b: ClashConfigCorrida): boolean {
  return chaveDoLado(a.sideA) === chaveDoLado(b.sideA) && chaveDoLado(a.sideB) === chaveDoLado(b.sideB)
}

function mm(m: number): string {
  return `${String(Math.round(m * 1000 * 100) / 100).replace('.', ',')} mm`
}

/** Porque e que a corrida actual nao e comparavel com a anterior, ou
 *  null se for. */
export function razaoNaoComparavel(
  anterior: ClashConfigCorrida | null,
  actual: ClashConfigCorrida,
): string | null {
  if (!anterior) return 'sem corrida anterior registada'
  if (Math.abs(anterior.tolerance - actual.tolerance) > 1e-9) {
    return `tolerância ${mm(actual.tolerance)} em vez de ${mm(anterior.tolerance)}`
  }
  if (Math.abs(anterior.clearance - actual.clearance) > 1e-9) {
    return `folga ${mm(actual.clearance)} em vez de ${mm(anterior.clearance)}`
  }
  if (anterior.selfClash !== actual.selfClash) {
    return 'teste dentro do mesmo modelo diferente'
  }
  const pa = anterior.penetracaoMinima ?? 0
  const pb = actual.penetracaoMinima ?? 0
  if (Math.abs(pa - pb) > 1e-9) {
    return `penetração mínima ${mm(pb)} em vez de ${mm(pa)}`
  }
  // Os cortes da folga so contam com a folga ligada.
  if (actual.clearance > 0) {
    const pares = (c: ClashConfigCorrida) => (c.folgaPares ? [...c.folgaPares].sort().join(', ') : 'todos os pares')
    if (pares(anterior) !== pares(actual)) {
      return `folga entre ${pares(actual)} em vez de ${pares(anterior)}`
    }
    const ca = anterior.ignoreContacts ?? 0
    const cb = actual.ignoreContacts ?? 0
    if (Math.abs(ca - cb) > 1e-9) {
      return `contactos ignorados abaixo de ${mm(cb)} em vez de ${mm(ca)}`
    }
  }
  if (!mesmosConjuntos(anterior, actual)) return 'conjuntos A e B diferentes'
  return null
}

/** Os dois modelos de uma chave canonica ("m1:1|m2:2|hard"). */
export function modelosDaChave(key: string): string[] {
  const partes = key.split('|')
  if (partes.length !== 3) return []
  return [...new Set(partes.slice(0, 2).map((p) => p.slice(0, p.lastIndexOf(':'))))]
}

/** DD/MM/AAAA de uma data ISO. */
export function dataCurtaPt(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`
}

export interface EntradaReconciliacao {
  triagem: ClashTriageMap
  /** Conflitos desta corrida. */
  resultados: readonly ClashResult[]
  configAnterior: ClashConfigCorrida | null
  configActual: ClashConfigCorrida
  /** modelIds carregados agora. */
  modelosCarregados: ReadonlySet<string>
  /** Onde esta este GlobalId: o modelo carregado que o tem, false se
   *  nenhum o tem, null se nao se sabe (conta como existente). */
  ondeEsta: (guid: string) => string | false | null
  /** ISO da corrida. */
  data: string
  autor: string
}

export interface SaidaReconciliacao {
  triagem: ClashTriageMap
  resumo: ResumoReconciliacao
  /** Chaves cuja entrada mudou. */
  alteradas: string[]
}

function comEvento(
  e: ClashTriageEntry,
  ev: ClashEvento,
  patch: Partial<ClashTriageEntry>,
  data: string,
): ClashTriageEntry {
  const out: ClashTriageEntry = {
    ...e,
    ...patch,
    updatedAt: data,
    eventos: [...(e.eventos ?? []), ev].slice(-200),
  }
  delete out.porVerificar
  return out
}

/** A geometria de um conflito, para o reconstruir quando deixar de
 *  ocorrer (exportar o topico ja fechado). */
export function ultimoDe(r: ClashResult): ClashUltimoConhecido {
  return {
    pair: r.pair,
    point: r.point,
    bbox: r.bbox,
    kind: r.kind,
    ...(r.depth !== undefined ? { depth: r.depth } : {}),
    ...(r.distance !== undefined ? { distance: r.distance } : {}),
    categoryA: r.categoryA ?? null,
    categoryB: r.categoryB ?? null,
  }
}

/** Guarda a geometria nas entradas indicadas que ainda nao a tem e cujo
 *  conflito esta nos resultados (triagem feita, ou proposta importada,
 *  depois da corrida). */
export function comUltimo(
  triagem: ClashTriageMap,
  keys: readonly string[],
  resultados: readonly ClashResult[],
): ClashTriageMap {
  if (keys.length === 0 || resultados.length === 0) return triagem
  const porKey = new Map(resultados.map((r) => [clashKey(r), r]))
  let out = triagem
  for (const k of keys) {
    const e = out[k]
    const r = porKey.get(k)
    if (!e || e.ultimo || !r) continue
    if (out === triagem) out = { ...triagem }
    out[k] = { ...e, ultimo: ultimoDe(r) }
  }
  return out
}

/** Aplica a reconciliacao. Nao toca nas entradas sem decisao. */
export function reconciliar(e: EntradaReconciliacao): SaidaReconciliacao {
  const presentes = new Map<string, ClashResult>()
  for (const r of e.resultados) presentes.set(clashKey(r), r)
  const dia = dataCurtaPt(e.data)
  const razaoConfig = razaoNaoComparavel(e.configAnterior, e.configActual)
  const triagem: ClashTriageMap = { ...e.triagem }
  const alteradas: string[] = []
  const resumo: ResumoReconciliacao = { resolvidos: 0, reabertos: 0, novos: 0, porVerificar: 0 }

  for (const [key, entry] of Object.entries(e.triagem)) {
    if (!temDecisao(entry)) continue
    const r = presentes.get(key)
    if (r) {
      // Continua a ocorrer. Guarda-se a geometria (para um dia voltar a
      // exportar o topico ja fechado) e tira-se o "por verificar".
      const ultimo = ultimoDe(r)
      if (entry.state === 'resolvido') {
        triagem[key] = {
          ...comEvento(
            entry,
            { data: e.data, tipo: 'reaberto', texto: `Reapareceu na corrida de ${dia}.`, autor: e.autor },
            { state: 'reaberto' },
            e.data,
          ),
          ultimo,
        }
        resumo.reabertos += 1
        alteradas.push(key)
      } else {
        const next: ClashTriageEntry = { ...entry, ultimo }
        if (next.porVerificar) {
          delete next.porVerificar
          alteradas.push(key)
        }
        triagem[key] = next
      }
      continue
    }

    // Deixou de ocorrer.
    if (entry.state === 'resolvido') continue
    const modelos = modelosDaChave(key)
    const naoCarregado = modelos.find((m) => !e.modelosCarregados.has(m))
    // Onde estao hoje os dois elementos (pelo GlobalId). Um elemento cujo
    // modelo foi substituido por outra revisao (outro nome) continua a
    // contar como carregado, se o GlobalId estiver num modelo aberto.
    const onde = (entry.guids ?? []).map((g) => e.ondeEsta(g))
    const emFalta = onde.some((x) => x === false)
    let razao = razaoConfig
    if (!razao && naoCarregado) {
      // O modelo da chave nao esta aberto: so se compara se os dois
      // elementos foram encontrados noutro modelo aberto.
      const todosEncontrados = onde.length === 2 && onde.every((x) => typeof x === 'string')
      if (!todosEncontrados) razao = `modelo ${naoCarregado} não carregado`
    }
    if (razao) {
      const porVerificar = `corrida não comparável: ${razao}`
      if (entry.porVerificar !== porVerificar) {
        triagem[key] = { ...entry, porVerificar }
        alteradas.push(key)
      }
      resumo.porVerificar += 1
      continue
    }
    // Com os modelos da chave abertos, um GlobalId que nenhum tem e um
    // elemento removido.
    const removido = emFalta
    const encontrados = [...new Set(onde.filter((x): x is string => typeof x === 'string'))]
    const revisoes = (encontrados.length > 0 ? encontrados : modelos).join(', ')
    const texto = removido
      ? `Deixou de ocorrer na corrida de ${dia}: elemento removido (revisões: ${revisoes}).`
      : `Deixou de ocorrer na corrida de ${dia} (revisões: ${revisoes}).`
    triagem[key] = comEvento(
      entry,
      { data: e.data, tipo: removido ? 'removido' : 'resolvido', texto, autor: e.autor },
      { state: 'resolvido' },
      e.data,
    )
    resumo.resolvidos += 1
    alteradas.push(key)
  }

  // Novos: os conflitos desta corrida que nao tem triagem (por triar).
  // Nao se compara com as chaves da corrida anterior: um conflito ja
  // visto mas nunca triado continua a ser trabalho por fazer, e um
  // triado nunca e "novo", mesmo com outra chave.
  for (const key of presentes.keys()) {
    const t = triagem[key]
    if (!t || !temDecisao(t)) resumo.novos += 1
  }
  return { triagem, resumo, alteradas }
}

/** Par de GlobalId e tipo: a identidade de um conflito entre revisoes. */
export function chaveDosGuids(guids: readonly [string, string], kind: string): string {
  return `${[...guids].sort().join('|')}|${kind}`
}

/**
 * Passa a triagem para as chaves desta corrida quando o mesmo par de
 * GlobalId (e o mesmo tipo) aparece com outra chave (outra revisao do
 * IFC, outros localId, ou outro nome de ficheiro). So migra entradas com
 * decisao cuja chave nao esta na corrida, e nunca por cima de uma chave
 * que ja tem decisao. Devolve tambem as chaves novas migradas, para nao
 * as contar como conflitos novos.
 */
export function migrarPorGuids(
  triagem: ClashTriageMap,
  actuais: readonly { key: string; guids: readonly [string | null, string | null]; kind: string }[],
): { triagem: ClashTriageMap; migradas: Map<string, string> } {
  const porGuids = new Map<string, string>()
  const presentes = new Set<string>()
  for (const a of actuais) {
    presentes.add(a.key)
    const [g1, g2] = a.guids
    if (g1 && g2) porGuids.set(chaveDosGuids([g1, g2], a.kind), a.key)
  }
  const out: ClashTriageMap = { ...triagem }
  const migradas = new Map<string, string>()
  for (const [key, entry] of Object.entries(triagem)) {
    if (presentes.has(key) || !temDecisao(entry) || !entry.guids) continue
    const kind = key.slice(key.lastIndexOf('|') + 1)
    const nova = porGuids.get(chaveDosGuids(entry.guids, kind))
    if (!nova || nova === key) continue
    const ocupada = out[nova]
    if (ocupada && temDecisao(ocupada)) continue
    out[nova] = { ...entry }
    delete out[key]
    migradas.set(key, nova)
  }
  return { triagem: out, migradas }
}

/** "Desde a ultima corrida: N resolvidos, M reabertos, K novos, J por
 *  verificar". */
export function fraseDoResumo(r: ResumoReconciliacao): string {
  return (
    `Desde a última corrida: ${r.resolvidos} resolvido${r.resolvidos === 1 ? '' : 's'}, `
    + `${r.reabertos} reaberto${r.reabertos === 1 ? '' : 's'}, `
    + `${r.novos} novo${r.novos === 1 ? '' : 's'}, `
    + `${r.porVerificar} por verificar.`
  )
}

/** Contagens por estado de uma triagem sobre os conflitos da corrida e
 *  os triados ausentes. */
export function contagensDaCorrida(
  resultados: readonly ClashResult[],
  triagem: ClashTriageMap,
): ClashContagens {
  const c: ClashContagens = { total: resultados.length, novo: 0, aceite: 0, resolvido: 0, reaberto: 0, porVerificar: 0 }
  for (const r of resultados) c[(triagem[clashKey(r)]?.state ?? 'novo')] += 1
  for (const e of Object.values(triagem)) if (e.porVerificar) c.porVerificar += 1
  return c
}

/** Pares resolvidos de um grupo da proposta: "x de y". */
export function progressoDoGrupo(
  triagem: ClashTriageMap,
  grupo: string,
): { resolvidos: number; total: number } {
  let resolvidos = 0
  let total = 0
  for (const e of Object.values(triagem)) {
    if (e.group !== grupo) continue
    total += 1
    if (e.state === 'resolvido') resolvidos += 1
  }
  return { resolvidos, total }
}

/** Conflitos triados que deixaram de ocorrer, reconstruidos a partir da
 *  ultima geometria conhecida: para a exportacao levar os topicos ja
 *  resolvidos (Closed), e as outras plataformas os fecharem. */
export function resultadosResolvidosAusentes(
  triagem: ClashTriageMap,
  presentes: ReadonlySet<string>,
): ClashResult[] {
  const out: ClashResult[] = []
  for (const [key, e] of Object.entries(triagem)) {
    if (presentes.has(key) || e.state !== 'resolvido' || !e.ultimo) continue
    out.push({
      pair: e.ultimo.pair,
      kind: e.ultimo.kind,
      categoryA: e.ultimo.categoryA ?? null,
      categoryB: e.ultimo.categoryB ?? null,
      ...(e.ultimo.depth !== undefined ? { depth: e.ultimo.depth } : {}),
      ...(e.ultimo.distance !== undefined ? { distance: e.ultimo.distance } : {}),
      point: e.ultimo.point,
      bbox: e.ultimo.bbox,
      triangles: 0,
    })
  }
  return out
}
